"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { clsx } from "clsx";
function TooltipProvider({ delayDuration = 0, ...props }) {
    return (_jsx(TooltipPrimitive.Provider, { "data-slot": "tooltip-provider", delayDuration: delayDuration, ...props }));
}
const TooltipClickContext = React.createContext({ openOnClick: false });
function Tooltip({ delayDuration = 400, openOnClick, open: controlledOpen, onOpenChange, ...props }) {
    const isClickable = Boolean(openOnClick);
    const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
    const isControlled = controlledOpen !== undefined;
    const open = isControlled ? controlledOpen : uncontrolledOpen;
    const handleOpenChange = (next) => {
        if (isClickable) {
            if (!isControlled)
                setUncontrolledOpen(next);
            onOpenChange?.(next);
            return;
        }
        onOpenChange?.(next);
    };
    const toggle = React.useCallback(() => {
        const next = !open;
        if (!isControlled)
            setUncontrolledOpen(next);
        onOpenChange?.(next);
    }, [open, isControlled, onOpenChange]);
    return (_jsx(TooltipProvider, { children: _jsx(TooltipClickContext.Provider, { value: { openOnClick: isClickable, toggle }, children: _jsx(TooltipPrimitive.Root, { "data-slot": "tooltip", delayDuration: delayDuration, ...(isClickable ? { open, onOpenChange: handleOpenChange } : { onOpenChange }), ...props }) }) }));
}
function TooltipTrigger({ className, onClick, ...props }) {
    const { openOnClick, toggle } = React.useContext(TooltipClickContext);
    const handleClick = (e) => {
        onClick?.(e);
        if (openOnClick) {
            e.preventDefault();
            e.stopPropagation();
            toggle?.();
        }
    };
    return _jsx(TooltipPrimitive.Trigger, { "data-slot": "tooltip-trigger", className: clsx("cursor-pointer", className), onClick: handleClick, ...props });
}
function TooltipContent({ className, sideOffset = 0, children, arrowClasses, showArrow = false, ...props }) {
    const container = typeof document !== 'undefined' ? document.getElementById('widget') : null;
    return (_jsx(TooltipPrimitive.Portal, { container: container, children: _jsx("div", { className: "layerswap-styles", children: _jsxs(TooltipPrimitive.Content, { "data-slot": "tooltip-content", sideOffset: sideOffset, className: clsx("z-50 origin-(--radix-tooltip-content-transform-origin) rounded-xl border border-secondary-600 bg-secondary-800 px-3 py-1.5 text-xs text-secondary-text has-data-[slot=kbd]:pr-1.5 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 **:data-[slot=kbd]:relative **:data-[slot=kbd]:isolate **:data-[slot=kbd]:z-50 **:data-[slot=kbd]:rounded-lg data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95", className), ...props, children: [showArrow && _jsx(TooltipArrow, { className: arrowClasses }), children] }) }) }));
}
function TooltipArrow({ className, ...props }) {
    return _jsx(TooltipPrimitive.Arrow, { className: clsx("bg-secondary-500 fill-secondary-500 z-50 size-2.5 translate-y-[calc(-50%-2px)] rotate-45 rounded-[2px] data-[side=left]:translate-x-[-1.5px] data-[side=right]:translate-x-[1.5px]", className), ...props });
}
export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, TooltipArrow };
